import React, { useState, useEffect } from 'react';
import Accordion from '../Accordion';
import { Checkbox } from '../Form/form-elements';
import categories from './categories.json';
import useAppContext from "../../hooks/useAppContext";
import styled from 'styled-components';
import closeIcon from '@images/icons/resoruces-mobile-filters-close.svg';
import openIcon from '@images/icons/resoruces-mobile-filters-open.svg';

// Accordion container component
const AccordionContainer = ({ children }) => (
	<div className="w-full [&_.accordion-button]:w-full [&_.accordion-button]:flex [&_.accordion-button]:justify-between [&_.accordion-button]:items-center">
		{children}
	</div>
);

const RefineByButton = styled.button`
	background-color: white;
	border: 1px solid #FFF;
	background: rgba(255, 255, 255, 0.80);
	border-radius: 100px;
	padding: 10px 20px;
	box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.05) inset, 0px 10px 50px 0px rgba(0, 0, 0, 0.10);
	backdrop-filter: blur(15px);
	background-repeat: no-repeat;
	background-position: right 20px center;
	&.open{
		background-image: url(${closeIcon});
	}
	&.close{
		background-image: url(${openIcon});
	}
`;

const FiltersContainer = styled.div`
	@media (max-width: 1280px) {
		padding: 0 20px;
		z-index: 9;
	}

	input[type="checkbox"]{
		background: transparent;
	}
`;

const MobileFilterPanel = styled.div`
	background: white;
	box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease-in-out;
	max-height: 0;
	opacity: 0;
	overflow: hidden;
	
	&.open {
		max-height: fit-content;
		opacity: 1;
		padding: 24px 32px 6px;
		margin-top: 10px;
		border-bottom-left-radius: 32px;
		border-bottom-right-radius: 32px;
	}
`;

// Category section component
const CategorySection = ({ title, items, selectedItems, onItemToggle }) => {
	const [isOpen, setIsOpen] = useState(true);

	const checkboxList = (
		<div className="space-y-2 py-4 [&>div]:py-2 [&>div]:m-0 [&_label]:text-dark_green">
			{items.map((item) => (
				<Checkbox
					key={item}
					id={`${title.toLowerCase()}-${item}`}
					name={`${title.toLowerCase()}-${item}`}
					label={item}
					checked={selectedItems.includes(item)}
					onChange={() => onItemToggle(title, item)}
				/>
			))}
		</div>
	);

	return (
		<AccordionContainer>
			<Accordion
				id={`${title.toLowerCase()}-section`}
				title={
					<div className="flex items-center justify-between w-full">
						<h5 className="text-dark_green text-p font-bold">{title}</h5>
						<svg 
							className={`w-5 h-5 text-green transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
							fill="none" 
							stroke="currentColor" 
							viewBox="0 0 24 24"
						>
							<path 
								strokeLinecap="round" 
								strokeLinejoin="round" 
								strokeWidth={2} 
								d="M19 9l-7 7-7-7"
							/>
						</svg>
					</div>
				}
				content={checkboxList}
				defaultOpen={true}
				className="py-[26px] border-t border-dark_green border-opacity-30 w-full"
				onToggle={(open) => setIsOpen(open)}
			/>
		</AccordionContainer>
	);
};

// Filter Categories component to avoid duplication
const FilterCategories = ({ selectedFilters, toggleItem }) => (
	<>
		<CategorySection
			title="Speciality"
			items={categories.Speciality}
			selectedItems={selectedFilters.Speciality}
			onItemToggle={toggleItem}
		/>

		<CategorySection
			title="Type"
			items={categories.Type}
			selectedItems={selectedFilters.Type}
			onItemToggle={toggleItem}
		/>

		<CategorySection
			title="Topic"
			items={categories.Topic}
			selectedItems={selectedFilters.Topic}
			onItemToggle={toggleItem}
		/>
	</>
);

const ResourcesSidebar = () => {
	// Use the app context
	const { resourceFilters, updateResourceFilters } = useAppContext();

	// Initialize local state from context or defaults
	const [selectedFilters, setSelectedFilters] = useState({
		Speciality: ["All"],
		Type: [],
		Topic: []
	});

	const [isOpen, setIsOpen] = useState(false);

	// Sync local state with context when context changes
	useEffect(() => {
		if (resourceFilters) {
			setSelectedFilters(resourceFilters);
		}
	}, [resourceFilters]);

	const toggleItem = (category, item) => {
		const currentSelected = [...selectedFilters[category]];
		let newSelection;

		// For Speciality category
		if (category === 'Speciality') {
			// If clicking "All", select only "All"
			if (item === 'All') {
				newSelection = ["All"];
			}
			// If clicking non-All item and it's already selected, go back to "All"
			else if (currentSelected.length === 1 && currentSelected[0] === item) {
				newSelection = ["All"];
			}
			// Otherwise select the new item (replacing current selection)
			else {
				newSelection = [item];
			}
		}
		// For Topic category
		else if (category === 'Topic') {
			// If clicking already selected item, deselect it (empty selection allowed)
			if (currentSelected.length === 1 && currentSelected[0] === item) {
				newSelection = [];
			}
			// Otherwise select the new item (replacing current selection)
			else {
				newSelection = [item];
			}
		}
		// For Type category (multiple selections)
		else {
			// Toggle behavior - if already selected, remove it; otherwise add it
			newSelection = currentSelected.includes(item)
				? currentSelected.filter(id => id !== item)
				: [...currentSelected, item];
		}

		// Create the updated filters object
		const updatedFilters = {
			...selectedFilters,
			[category]: newSelection
		};

		// Update both local state and context
		setSelectedFilters(updatedFilters);
		updateResourceFilters(updatedFilters);
	};

	const openMobileFilters = (value) => {
		setIsOpen(value);
	}

	return (
		<FiltersContainer className='relative'>
			{/* Mobile View */}
			<div className='mobilefilters xl:hidden flex flex-col items-center justify-center'>
				<RefineByButton
					className={`text-dark_green text-p font-bold h-[50px] w-full sectionNav-gradient-light bg-white/80 px-8 py-4 rounded-full z-20 text-left ${isOpen ? 'open' : 'close'}`}
					onClick={() => openMobileFilters(!isOpen)}
				>
					Refine By
				</RefineByButton>

				<MobileFilterPanel className={isOpen ? 'open  absolute top-[15px] left-[20px] w-[calc(100%-40px)] z-10' : ''}>
					<FilterCategories selectedFilters={selectedFilters} toggleItem={toggleItem} />
				</MobileFilterPanel>
			</div>

			{/* Desktop View */}
			<div className="desktopfilters xl:block hidden pl-[40px] lg:pt-[68px] lg:pb-[118px]">
				<h4 className="text-dark_green text-h4_mobile lg:text-h4 mb-[40px]">Refine by</h4>
				<FilterCategories selectedFilters={selectedFilters} toggleItem={toggleItem} />
			</div>
		</FiltersContainer>
	);
};

export default ResourcesSidebar; 